/**
 * InventoryStocktakingPage — 재고 실사 관리 (W19-C4, SC5 + apply/reject 버튼).
 *
 * 【역할】
 * - tb_inventory_stocktaking 상태 전이: PENDING → APPLIED / REJECTED.
 * - apply 시 tb_inventory.quantity 를 actual_qty 로 UPSERT (원자적).
 * - reject 시 이력만 유지, 재고 변동 없음.
 *
 * 【주요 기능】
 * - 조회: status + warehouseId 필터
 * - 신규 실사 Modal: warehouse, product, expected(선택), actual, note
 * - 선택 건 apply / reject
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useLabel (다국어)
 * - useI18nTickStore (locale 전환 re-render)
 *
 * 【Backend RPC serviceName】
 * - inventory_stocktaking/searchList (search)
 * - inventory_stocktaking/create (save)
 * - inventory_stocktaking/apply (save)
 * - inventory_stocktaking/reject (save)
 *
 * 【DataSet 구조】
 * - ds_stocktake: 실사 목록 (PK stocktakeId)
 *
 * 【사용처】 router/index.tsx — `{ path: 'inventory-stocktaking' }`
 * 【포팅 원본】 (없음 — Wave 15 백엔드 있었으나 프론트 미구현. Wave 19 에서 이식.)
 */
import { useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Modal, InputNumber, Select, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useLabel } from '@/framework/useLabel'
import { useI18nTickStore } from '@/framework/useLocale'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'

interface StocktakeRow {
  stocktakeId?: number
  warehouseId?: number
  productId?: number
  expectedQty?: number
  actualQty?: number
  diffQty?: number
  status?: string
  checkedBy?: string
  note?: string
}

interface WarehouseOption {
  warehouseId: number
  warehouseCode: string
  warehouseName: string
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'gold',
  APPLIED: 'green',
  REJECTED: 'default'
}

/**
 * InventoryStocktakingPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function InventoryStocktakingPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)

  const ds_stocktake = $createDataSet<StocktakeRow>([])
  const [searchForm] = Form.useForm()
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm] = Form.useForm<StocktakeRow>()
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null)

  const loadWarehouses = async () => {
    const res = await $transaction({
      id: 'wh-lookup',
      service: 'warehouse/searchList',
      action: 'search',
      inds: { ds_search: { isActive: 1 } }
    })
    if (res.success) {
      const ds = res.data?.ds_warehouse as { rows?: WarehouseOption[] } | undefined
      setWarehouses(ds?.rows ?? [])
    }
  }

  const fn_search = async () => {
    setLoading(true)
    const res = await $transaction({
      id: 'st-search',
      service: 'inventory_stocktaking/searchList',
      action: 'search',
      inds: { ds_search: searchForm.getFieldsValue() },
      outds: { ds_stocktake }
    })
    setLoading(false)
    if (res.success) setSelectedKey(null)
  }

  useEffect(() => {
    loadWarehouses()
    fn_search()
  }, [])

  const fn_create = () => {
    createForm.resetFields()
    createForm.setFieldsValue({ actualQty: 0 })
    setCreateOpen(true)
  }

  const fn_createSubmit = async () => {
    const v = await createForm.validateFields()
    const res = await $transaction({
      id: 'st-create',
      service: 'inventory_stocktaking/create',
      action: 'save',
      inds: { ds_stocktake: { rows: [{ ...v, _state: 'C' }] } }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      setCreateOpen(false)
      fn_search()
    }
  }

  const pickSelected = (): StocktakeRow | null => {
    const row = ds_stocktake.rows.find((r) => r._rowId === selectedKey)
    return row?.data ?? null
  }

  const fn_apply = async () => {
    const st = pickSelected()
    if (!st?.stocktakeId) return $msg.byId('MSG_SELECT_ROW')
    $msg.confirmById('MSG_CONFIRM_APPLY', async () => {
      const res = await $transaction({
        id: 'st-apply',
        service: 'inventory_stocktaking/apply',
        action: 'save',
        inds: { ds_search: { stocktakeId: st.stocktakeId } }
      })
      if (res.success) {
        $msg.byId('MSG_APPLY_SUCCESS')
        fn_search()
      }
    })
  }

  const fn_reject = async () => {
    const st = pickSelected()
    if (!st?.stocktakeId) return $msg.byId('MSG_SELECT_ROW')
    $msg.confirmById('MSG_CONFIRM_REJECT', async () => {
      const res = await $transaction({
        id: 'st-reject',
        service: 'inventory_stocktaking/reject',
        action: 'save',
        inds: { ds_search: { stocktakeId: st.stocktakeId } }
      })
      if (res.success) {
        $msg.byId('MSG_APPLY_SUCCESS')
        fn_search()
      }
    })
  }

  const warehouseLabel = (wid?: number) => {
    const w = warehouses.find((x) => x.warehouseId === wid)
    return w ? `${w.warehouseCode} — ${w.warehouseName}` : (wid ?? '-')
  }

  const visibleRows = ds_stocktake.getVisibleRows()
  const columns: ColumnsType<(typeof visibleRows)[number]> = [
    { title: t('COL_STOCKTAKE_ID', '실사번호'), dataIndex: ['data', 'stocktakeId'], width: 110 },
    {
      title: t('COL_WAREHOUSE', '창고'),
      dataIndex: ['data', 'warehouseId'],
      width: 200,
      render: (v) => warehouseLabel(v)
    },
    { title: t('COL_PRODUCT_ID', '상품ID'), dataIndex: ['data', 'productId'], width: 110 },
    { title: t('COL_EXPECTED_QTY', '예상수량'), dataIndex: ['data', 'expectedQty'], width: 100 },
    { title: t('COL_ACTUAL_QTY', '실측수량'), dataIndex: ['data', 'actualQty'], width: 100 },
    {
      title: t('COL_DIFF_QTY', '차이'),
      dataIndex: ['data', 'diffQty'],
      width: 100,
      render: (v: number) => {
        if (v === undefined || v === null) return '-'
        const color = v > 0 ? 'green' : v < 0 ? 'red' : 'default'
        return <Tag color={color}>{v > 0 ? `+${v}` : v}</Tag>
      }
    },
    {
      title: t('COL_STATUS', '상태'),
      dataIndex: ['data', 'status'],
      width: 120,
      render: (s: string) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag>
    },
    { title: t('COL_CHECKED_BY', '실사자'), dataIndex: ['data', 'checkedBy'], width: 120 },
    { title: t('COL_NOTE', '비고'), dataIndex: ['data', 'note'] }
  ]

  const whOptions = warehouses.map((w) => ({
    label: `${w.warehouseCode} - ${w.warehouseName}`,
    value: w.warehouseId
  }))

  const selected = pickSelected()
  const canTransition = selected?.status === 'PENDING'

  return (
    <>
      <Alert
        type="warning"
        showIcon
        message={t(
          'MSG_STOCKTAKE_INFO',
          '재고 실사 — apply 시 tb_inventory 의 실제 수량이 actualQty 로 덮어씌워집니다 (원자적).'
        )}
        style={{ marginBottom: 12 }}
      />
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          loading,
          content: (
            <>
              <Form.Item name="status" label={t('LBL_STATUS', '상태')}>
                <Select
                  allowClear
                  style={{ width: 140 }}
                  options={['PENDING', 'APPLIED', 'REJECTED'].map((s) => ({ label: s, value: s }))}
                />
              </Form.Item>
              <Form.Item name="warehouseId" label={t('LBL_WAREHOUSE', '창고')}>
                <Select allowClear style={{ width: 200 }} options={whOptions} />
              </Form.Item>
            </>
          )
        }}
        toolbar={
          <Space>
            <Button onClick={fn_search} loading={loading}>
              {t('BTN_SEARCH', '조회')}
            </Button>
            <Button type="primary" onClick={fn_create}>
              {t('BTN_NEW_STOCKTAKE', '신규 실사')}
            </Button>
            <Button onClick={fn_apply} disabled={!canTransition} type="primary">
              {t('BTN_APPLY', '승인')}
            </Button>
            <Button onClick={fn_reject} disabled={!canTransition} danger>
              {t('BTN_REJECT', '거부')}
            </Button>
          </Space>
        }
      >
        <DataTable
          rowKey={(r) => r._rowId}
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedKey ? [selectedKey] : [],
            onChange: (keys) => setSelectedKey(keys[0] ?? null)
          }}
        />
      </AppPage>

      <Modal
        open={createOpen}
        title={t('TTL_NEW_STOCKTAKE', '신규 실사 등록')}
        onOk={fn_createSubmit}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="warehouseId"
            label={t('LBL_WAREHOUSE', '창고')}
            rules={[{ required: true }]}
          >
            <Select options={whOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item
            name="productId"
            label={t('LBL_PRODUCT_ID', '상품ID')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item
            name="expectedQty"
            label={t('LBL_EXPECTED_QTY', '예상수량 (미입력 시 현재 재고 자동 조회)')}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item
            name="actualQty"
            label={t('LBL_ACTUAL_QTY', '실측수량')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="note" label={t('LBL_NOTE', '비고')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
